import { useCallback, useMemo } from "react"
import { EventGroup } from "sharedTypes"
import { useEmails } from "queries/emails"
import { useForms } from "queries/forms"
import { useEventQuestions } from "queries/questions"
import { useDistinctPersonValues } from "queries/guests"
import { buildFormQuestionConfigs, resolveFieldConfig } from "./config"
import { FilterFieldConfig, FilterOptionsContext } from "./types"

// Generous enough that a realistic event's full question list fits in one
// page, so the sub-picker's client-side search isn't searching a partial set.
const QUESTIONS_PAGE_SIZE = 100

// Shared option-fetching + config-resolution for every ChipRow consumer
// (table-view GuestFilters and the SegmentForm modal) — keeps the two from
// drifting out of sync on which dynamic field types (forms, form questions,
// company/job title) they each support.
const useFilterOptionsContext = (eventId: string, eventGroups: EventGroup[]) => {
  const { data: emails = [] } = useEmails(eventId)
  const sentEmails = useMemo(() => (emails ?? []).filter((e) => e.sentOn), [emails])
  const { data: formsResult } = useForms(eventId)
  const forms = formsResult?.data ?? []
  const {
    questions: formQuestions, fetchNextPage: fetchMoreQuestions,
  } = useEventQuestions(eventId, QUESTIONS_PAGE_SIZE)
  const distinctPersonValues = useDistinctPersonValues(eventId)

  // Per-question configs are used only by resolveFieldConfig to look up the
  // config for an existing chip — the question sub-picker (step 2 in
  // AddFilterPopover) builds its own per-question config on the fly once a
  // question is chosen.
  const formQuestionConfigs = useMemo(
    () => buildFormQuestionConfigs(formQuestions),
    [formQuestions],
  )

  const optionsContext: FilterOptionsContext = useMemo(() => ({
    eventGroups,
    emails: sentEmails,
    forms,
    formQuestions,
    distinctPersonValues,
  }), [eventGroups, sentEmails, forms, formQuestions, distinctPersonValues])

  const getConfig = useCallback(
    (field: string): FilterFieldConfig | undefined => (
      resolveFieldConfig(field, formQuestionConfigs)
    ),
    [formQuestionConfigs],
  )

  return {
    optionsContext, formQuestionConfigs, getConfig, fetchMoreQuestions,
  }
}

export default useFilterOptionsContext
